import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import { Form, Input, Select, Switch } from '@engloba-tech/englobity';

import { NAME_SPACE } from 'i18n';
import {
  FormContainer,
  isSkeletonLoading,
  useFormState,
  LocaleDatePicker,
  NumberFormatCustom,
  ORDER_STATUSES,
  ORDER_SERIAL_NUMBERS,
  TAG_TYPES,
  enumHelper
} from 'shared';
import { DateRange } from '@material-ui/icons';
import { ViewOrderHistory } from '../ViewOrderHistory';

export function FormOrder({ formRef, order, onChangeInput }) {
  const [inputs, setInputs] = useFormState({ ...order } || {}, onChangeInput);
  const { t } = useTranslation(NAME_SPACE.ORDERS);

  useEffect(() => {
    setInputs({ ...order }, false);
  }, [order]);

  const orderStatuses = useMemo(() => {
    return enumHelper.enumToCombo(ORDER_STATUSES, t, 'statuses', false);
  }, [t]);

  const orderSerialNumbers = useMemo(() => {
    return enumHelper.enumToCombo(ORDER_SERIAL_NUMBERS, t, 'serialNumbers', false);
  }, [t]);

  const tagTypes = useMemo(() => {
    return enumHelper.enumToCombo(TAG_TYPES, t, 'tagTypes', false);
  }, [t]);

  return (
    <>
      <Form elementRef={formRef} onSubmit={() => null} autoComplete="off">
        <FormContainer grow={false}>
          <Grid item xs={12} md={12} lg={6}>
            <Input
              isLoading={isSkeletonLoading(inputs.installation)}
              required
              disabled
              name={t('properties.installation')}
              label={t('properties.installation')}
              variant="outlined"
              value={inputs.installation || ''}
              inputProps={{ 'aria-label': t('properties.installation') }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <LocaleDatePicker
              isLoading={isSkeletonLoading(inputs.entryDate)}
              required
              disabled
              withHours
              icon={<DateRange />}
              name={t('properties.entryDate')}
              label={t('properties.entryDate')}
              value={inputs.entryDate}
              inputProps={{ 'aria-label': 'entryDate' }}
              validators={['required']}
              errorMessages={[t('validations.required')]}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Select
              className="select"
              isLoading={isSkeletonLoading(orderStatuses[0]) || isSkeletonLoading(inputs.status)}
              elements={orderStatuses}
              value={inputs.status}
              title={t('properties.status')}
              disabled={true}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Input
              isLoading={isSkeletonLoading(inputs.client)}
              required
              disabled
              name={t('properties.client')}
              label={t('properties.client')}
              variant="outlined"
              value={inputs.client || ''}
              inputProps={{ 'aria-label': t('properties.client') }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Input
              isLoading={isSkeletonLoading(inputs.experience)}
              required
              disabled
              name={t('properties.experience')}
              label={t('properties.experience')}
              variant="outlined"
              value={inputs.experience || ''}
              inputProps={{ 'aria-label': t('properties.experience') }}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              isLoading={isSkeletonLoading(inputs.supplierOrder)}
              required
              disabled
              name={t('properties.supplierOrder')}
              label={t('properties.supplierOrder')}
              variant="outlined"
              value={inputs.supplierOrder || ''}
              inputProps={{ 'aria-label': t('properties.supplierOrder') }}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              isLoading={isSkeletonLoading(inputs.supplierReference)}
              required
              disabled
              name={t('properties.supplierReference')}
              label={t('properties.supplierReference')}
              variant="outlined"
              value={inputs.supplierReference || ''}
              inputProps={{ 'aria-label': t('properties.supplierReference') }}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Input
              isLoading={isSkeletonLoading(inputs.customerReference)}
              required
              disabled
              name={t('properties.customerReference')}
              label={t('properties.customerReference')}
              variant="outlined"
              value={inputs.customerReference || ''}
              inputProps={{ 'aria-label': t('properties.customerReference') }}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Select
              className="select"
              isLoading={isSkeletonLoading(tagTypes[0]) || isSkeletonLoading(inputs.tag)}
              elements={tagTypes}
              value={inputs.tag}
              title={t('properties.tag')}
              disabled={true}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Input
              isLoading={isSkeletonLoading(inputs.tagAmount)}
              name={t('properties.tagAmount')}
              disabled
              label={t('properties.tagAmount')}
              variant="outlined"
              InputProps={{ inputComponent: NumberFormatCustom, inputProps: { suffix: '' } }}
              value={inputs.tagAmount ?? ''}
              required
              validators={['required', 'minNumber: 1', 'noDecimal']}
              errorMessages={[t('validations.required'), t('validations.minNumber'), t('validations.noDecimal')]}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <Select
              className="select"
              isLoading={isSkeletonLoading(orderSerialNumbers[0]) || isSkeletonLoading(inputs.serialNumber)}
              elements={orderSerialNumbers}
              value={inputs.serialNumber}
              title={t('properties.serialNumber')}
              disabled={true}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Switch label={t('properties.multilanguage')} checked={inputs.multilanguage} disabled />
          </Grid>
        </FormContainer>
        <FormContainer>
          <Grid item xs={12}>
            <ViewOrderHistory orderHistory={inputs?.history} />
          </Grid>
        </FormContainer>
      </Form>
    </>
  );
}

FormOrder.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  order: PropTypes.shape(),
  onChangeInput: PropTypes.func
};
